(() => {
  const GLOBAL_KEY = 'GuerayImageLightboxV2';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan(document);
    return;
  }

  class GuerayImageLightbox {
    constructor(root) {
      if (!root || root.dataset.gyImageLightboxV2Ready === 'true') return;

      this.root = root;
      this.triggers = Array.from(root.querySelectorAll('.gy-image-zoom'));
      this.lightbox = root.querySelector('[data-gy-image-lightbox-v2-modal]');
      if (!this.triggers.length || !this.lightbox) return;

      this.root.dataset.gyImageLightboxV2Ready = 'true';
      this.imageFrame = this.lightbox.querySelector('.gy-image-lightbox__image-frame');
      this.closeTargets = Array.from(this.lightbox.querySelectorAll('[data-gy-image-close]'));
      this.originalParent = this.lightbox.parentNode;
      this.useInstantPreview = this.lightbox.classList.contains('gy-b03-lightbox');
      this.previewRequestId = 0;
      this.image = document.createElement('img');
      this.image.className = 'gy-image-lightbox__image';
      this.image.loading = 'eager';
      this.image.decoding = 'async';
      this.image.draggable = false;
      this.imageFrame?.appendChild(this.image);

      this.lastTrigger = null;
      this.closeTimer = 0;
      this.previousRootOverflow = '';
      this.previousBodyOverflow = '';
      this.suppressClickUntil = 0;

      this.bind();
    }

    bind() {
      this.image.addEventListener('load', () => {
        this.lightbox.classList.remove('is-loading');
      });
      this.image.addEventListener('error', () => {
        this.lightbox.classList.remove('is-loading');
      });

      this.triggers.forEach((trigger) => this.bindTrigger(trigger));
      this.closeTargets.forEach((target) => {
        target.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.lightbox.hidden) this.close();
      });

      if (this.useInstantPreview) {
        window.addEventListener('resize', () => {
          if (!this.lightbox.hidden && this.previewRatio) this.sizePreview();
        }, { passive: true });
      }
    }

    bindTrigger(trigger) {
      let touchStart = null;

      trigger.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
        trigger.classList.add('is-touching');
      }, { passive: true });

      trigger.addEventListener('touchend', (event) => {
        const start = touchStart;
        touchStart = null;
        window.setTimeout(() => trigger.classList.remove('is-touching'), 180);
        if (!start || event.changedTouches.length !== 1) return;

        const touch = event.changedTouches[0];
        const movedX = Math.abs(touch.clientX - start.x);
        const movedY = Math.abs(touch.clientY - start.y);
        const elapsed = Date.now() - start.time;
        if (movedX > 10 || movedY > 10 || elapsed > 700) return;

        event.preventDefault();
        event.stopPropagation();
        this.suppressClickUntil = Date.now() + 500;
        this.open(trigger);
      }, { passive: false });

      trigger.addEventListener('touchcancel', () => {
        touchStart = null;
        trigger.classList.remove('is-touching');
      }, { passive: true });

      trigger.addEventListener('click', (event) => {
        if (Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          return;
        }
        this.open(trigger);
      });
    }

    sourceFor(trigger) {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      if (mobile) {
        return trigger.getAttribute('data-gy-zoom-mobile-src') || trigger.getAttribute('data-gy-zoom-src');
      }
      return trigger.getAttribute('data-gy-zoom-src');
    }

    sizePreview() {
      const width = document.documentElement.clientWidth;
      const height = window.visualViewport?.height || window.innerHeight;
      const mobile = width <= 640;
      const gutter = mobile ? 8 : Math.max(16, Math.min(width * 0.03, 36));
      const framePadding = mobile ? 12 : 18;
      const maxWidth = mobile
        ? width - gutter * 2 - framePadding
        : Math.min(width - gutter * 2 - framePadding, width * 0.94 - framePadding, 1422);
      const maxHeight = mobile
        ? height - gutter * 2 - framePadding
        : Math.min(height - gutter * 2 - framePadding, height - Math.max(48, Math.min(width * 0.07, 88)));
      const frameWidth = Math.max(1, Math.floor(Math.min(maxWidth, maxHeight * this.previewRatio)));
      this.imageFrame.style.setProperty('--gy-preview-width', `${frameWidth}px`);
      this.imageFrame.style.setProperty('--gy-preview-height', `${Math.max(1, Math.floor(frameWidth / this.previewRatio))}px`);
    }

    preparePreview(trigger, src) {
      const thumbnail = trigger.querySelector('img');
      const width = Number(thumbnail?.getAttribute('width')) || thumbnail?.naturalWidth || 1;
      const height = Number(thumbnail?.getAttribute('height')) || thumbnail?.naturalHeight || 1;
      const previewSrc = thumbnail?.currentSrc || thumbnail?.src;
      const requestId = ++this.previewRequestId;
      this.previewRatio = width / height;
      this.sizePreview();
      this.lightbox.classList.remove('is-loading');
      this.image.src = previewSrc || src;
      if (!previewSrc || this.image.src === new URL(src, document.baseURI).href) return;

      const fullImage = new Image();
      fullImage.decoding = 'async';
      fullImage.onload = async () => {
        try { await fullImage.decode(); } catch (_) {}
        if (requestId !== this.previewRequestId || this.lightbox.hidden) return;
        this.image.src = src;
      };
      fullImage.src = src;
    }

    open(trigger) {
      const src = this.sourceFor(trigger);
      if (!src || !this.image) return;

      window.clearTimeout(this.closeTimer);
      const alreadyOpen = !this.lightbox.hidden && this.lightbox.getAttribute('aria-hidden') === 'false';
      this.lastTrigger = trigger;
      if (this.lightbox.parentNode !== document.body) document.body.appendChild(this.lightbox);
      this.image.alt = trigger.getAttribute('data-gy-zoom-alt') || '';
      if (this.useInstantPreview) {
        this.preparePreview(trigger, src);
      } else {
        this.lightbox.classList.add('is-loading');
        this.image.src = src;
      }
      if (!this.useInstantPreview || !alreadyOpen) {
        this.previousRootOverflow = document.documentElement.style.overflow;
        this.previousBodyOverflow = document.body.style.overflow;
      }
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.lightbox.hidden = false;
      this.lightbox.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(() => {
        if (this.useInstantPreview && this.lightbox.getAttribute('aria-hidden') !== 'false') return;
        this.lightbox.classList.add('is-open');
      });

      const closeButton = this.lightbox.querySelector('.gy-image-lightbox__close');
      closeButton?.focus({ preventScroll: true });
    }

    close() {
      if (this.lightbox.hidden) return;
      this.previewRequestId++;

      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = this.previousRootOverflow;
      document.body.style.overflow = this.previousBodyOverflow;
      this.closeTimer = window.setTimeout(() => {
        this.lightbox.hidden = true;
        this.lightbox.classList.remove('is-loading');
        this.image.removeAttribute('src');
        if (this.originalParent?.isConnected) this.originalParent.appendChild(this.lightbox);
        this.lastTrigger?.focus({ preventScroll: true });
      }, 220);
    }
  }

  const api = {
    scan(scope = document) {
      scope.querySelectorAll('[data-gy-image-lightbox-v2]').forEach((root) => {
        new GuerayImageLightbox(root);
      });
    },
  };

  window[GLOBAL_KEY] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => api.scan(document), { once: true });
  } else {
    api.scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => api.scan(event.target));
})();
